import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { useEffect, useState } from 'react'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import AddDocumentModal from './add-document-modal'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { createDuplicateMeeting, deleteMeetings, getMeetingLists } from '../core/_requests'
import { getDaysDifference, sanitizeFileName } from '../core/_util'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React from "react"
import { useAuth } from '../../auth'
import NoDataPage from '../../widgets/components/NoDataPage'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { DeleteMeetingConfirmationModal } from './CancelConfimrationModal'
import { toast } from 'react-toastify'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useTimeZone } from '../../../hooks/useTimeZone'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import TagManager from 'react-gtm-module'
import { useThemeMode } from '../../../../_metronic/partials'

export const ComplianceMeetingTable = ({ sideBarTitle, search, meetingList, filter, setUpdate, update, clear }: any) => {
  const { currentUser, companyId } = useAuth()
  const userId = currentUser?.userId
  const navigator = useNavigate()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const { getDateValue } = useDateFormat()
  const { getTimeZoneValue } = useTimeZone()
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [activeModal, setActiveModal] = useState<'none' | 'AddDocumentModal'>('none')
  const [meeting, setMeeting] = useState<any>()
  const [loading, setLoading] = useState(false)
  const openModal = (modalType: 'AddDocumentModal') => {
    setActiveModal(modalType)
  }

  const closeModal = () => {
    setActiveModal('none')
  }
  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
  }
  const timeZone = localStorage.getItem('timeZone')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [id, setId] = useState('')
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [isActive, setIsActive] = useState(false) // eslint-disable-line
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const nameMatch = rankItem(`${row.original.meetingTitle}`, search)
    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }

  const [meetingsData, setMeetingsData] = useState<any[]>([])

  const getMeetingList = async () => {
    try {
      setLoading(true)
      const {
        data: { success, data },
      } = await getMeetingLists(sideBarTitle, '', '', update ? 1 : pagination.currentPage,
        pagination.pageSize)
      if (success) {
        setUpdate(false)
        setMeetingsData(data.data.meetingList);
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination?.currentPage,
          pageSize: data.pagination?.pageSize,
          totalCount: data.pagination?.totalRecords,
          nextPage: data.pagination?.nextPage,
          prevPage: data.pagination?.prevPage,
          totalPages: data.pagination?.totalPages,
        }))
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }
  const cancelMeeting = async () => {
    setCancelLoading(true)
    try {
      const {
        data: { success, error },
      } = await deleteMeetings(id)
      if (success) {
        getMeetingList()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.BoardMeetingCancelledSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              meeting_id: id,
            },
          },
        })
      }
      else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.BoardMeetingCancelFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              meeting_id: id,
              error: error[0]
            },
          },
        })
      }
    } catch (err) {
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.BoardMeetingCancelFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            meeting_id: id,
            error: err
          },
        },
      })
    }
    finally {
      setCancelLoading(false)
      setShowCancelModal(false)

    }
  }
  const createDuplicate = async (meetingId: any) => {
    toast.success(formatMessage({ id: 'Please wait while we take you to the edit page' }))
    try {
      const {
        data: { success, data, error },
      } = await createDuplicateMeeting(meetingId, companyId)
      if (success) {
        getMeetingList()
        toast.success(formatMessage({ id: 'Meeting duplicated successfully.' }))
        navigator(`${location.pathname}/view/add-board-meeting?tab=${sanitizeFileName(data.category).toLowerCase()}${`&meetingId=${data.meetingId}`}`)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DuplicateMeetingCreateSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              meeting_id: meetingId,
            },
          },
        })
      }
      else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DuplicateMeetingCreateFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              meeting_id: meetingId,
              error: error[0]
            },
          },
        })
      }
    } catch (err) {
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DuplicateMeetingCreateFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            meeting_id: meetingId,
            error: err
          },
        },
      })
    }
  }
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'meetingTitle',
        id: 'MeetingTitle',
        header: 'Meeting Details',
        cell: (info) => {
          const { meetingTitle, meetingDate, startTime, meetingParticipants, schedule, meetingId } = info.row.original;
          const acceptedLength = meetingParticipants.filter((data: any) => data.status === 'accept')
          return (
            <Link to={`../view/after-meeting?meetingId=${meetingId}`} className='text-dark'>
              <div className='d-flex align-items-center'>
                <div style={{ borderLeft: `3px solid ${!schedule ? 'red' : acceptedLength.length === 0 ? 'blue' : acceptedLength.length === meetingParticipants.length ? 'green' : 'yellow'}` }}>
                  <div className='ps-2'>
                    <div className='font-weight-500 font-size-16'>
                      {meetingTitle}
                    </div>
                    <div>{getDateValue(meetingDate.toString())} {` |  ${getTimeZoneValue(startTime.toString())}`} {`(${timeZone})`}</div>
                  </div>
                </div>
              </div >
            </Link>
          );
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'participants',
        id: 'Participants',
        header: 'Participants',
        cell: (info) => {
          const { meetingParticipants, meetingId } = info.row.original;
          return (<Link to={`../view/after-meeting?meetingId=${meetingId}`} className='text-dark'>
            <div className='px-3'>{meetingParticipants.length}</div>
          </Link>
          )

        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'Organizer',
        id: 'Organizer',
        header: 'Organizer',
        cell: (info) => {
          const { meetingParticipants, meetingId } = info.row.original;
          const name = meetingParticipants.find((data: any) => data.type === 'Organizer')?.name
          return (<Link to={`../view/after-meeting?meetingId=${meetingId}`} className='text-dark'><p className='mb-4 font-size-16 font-weight-500'>{name}</p></Link>);
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'status',
        id: 'Attendance',
        header: 'Attendance',
        cell: (info) => {
          const { meetingParticipants, schedule, meetingId } = info.row.original;
          const acceptedLength = meetingParticipants.filter((data: any) => data.status === 'accept')
          const cancelLength = meetingParticipants.filter((data: any) => data.status === 'reject')
          const getStatus = () => {
            if (!schedule) return 'Draft';
            if (acceptedLength.length === 0) return 'Invited';
            if (acceptedLength.length === meetingParticipants.length) return 'Accepted';
            if (acceptedLength.length > 0) return 'Partially Accepted';
            if (meetingParticipants.length === cancelLength.length) return 'Rejected';
            return 'Draft';
          };
          return (
            <Link to={`../view/after-meeting?meetingId=${meetingId}`} className='text-dark'>
              <div
                className={`badge py-2 px-6 ${!schedule ? 'alert-danger' : acceptedLength.length === 0 ? 'alert-primary' : acceptedLength.length === meetingParticipants.length ? 'alert-success' : 'alert-warning'}`}
              >
                <span className="flex-root">{getStatus()}</span>
              </div>
            </Link>
          )
        },
        filterFn: 'includesString',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const { meetingId, createdBy, meetingDate, category, type } = row.original;
          const IsCurrent = getDaysDifference(meetingDate)

          return (
            <div className='dropdown-container d-flex justify-content-center'>
              <Dropdown
                show={meetingId === selectedIndex}
                onToggle={(isOpen, event) => {
                  if (event.source === 'click') {
                    setSelectedIndex(isOpen ? meetingId : undefined)
                  } else {
                    setSelectedIndex(undefined)
                  }
                }}
              >
                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                  <img src={threeDots} alt='' className='three-dots-image' />
                </Dropdown.Toggle>
                <Dropdown.Menu className='m-2'>
                  <Dropdown.Item className='text-primary p-0'>
                    <Link to={`../view/after-meeting?meetingId=${meetingId}`} className='text-dark'>
                      <p className='mb-0 ps-5 py-3'>{formatMessage({ id: 'View' })}</p>
                    </Link>
                  </Dropdown.Item>
                  {userId === createdBy && IsCurrent >= 0 &&
                    <>
                      <Dropdown.Item className='text-dark ps-5'>
                        <Link to={`../view/add-board-meeting?tab=${sanitizeFileName(category).toLowerCase()}&meetingId=${meetingId}&type=${type}`} className='text-dark'>
                          {formatMessage({ id: 'Edit Meeting' })}
                        </Link>
                      </Dropdown.Item>
                    </>
                  }
                  {IsCurrent < 0 && <Dropdown.Item
                    className='text-dark ps-5'
                    onClick={() => {
                      setMeeting(row.original)
                      openModal('AddDocumentModal')
                    }}
                  >
                    {formatMessage({ id: 'Add Documents' })}
                  </Dropdown.Item>}
                  {IsCurrent > 0 &&
                    <Dropdown.Item className='text-dark ps-5'
                      onClick={() => {
                        createDuplicate(meetingId)
                      }}
                    >
                      {formatMessage({ id: 'Duplicate Meeting' })}
                    </Dropdown.Item>}
                  {userId === createdBy &&
                    <>
                      <Dropdown.Item className='text-danger ps-5' onClick={() => {
                        setShowCancelModal(true)
                        setId(meetingId)
                      }}>
                        <p className='mb-0'>{formatMessage({ id: 'Delete Meeting' })}</p>
                      </Dropdown.Item>
                    </>
                  }
                </Dropdown.Menu>
              </Dropdown>
            </div >
          );
        },
      },
    ],
    [selectedIndex] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const table = useReactTable({
    data: meetingsData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter: globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  useEffect(() => {
    setGlobalFilter(search);
  }, [search]);

  useEffect(() => {
    if (!filter && clear)
      getMeetingList()
  }, [// eslint-disable-line react-hooks/exhaustive-deps
    pagination.currentPage,
    pagination.pageSize,
    isActive,
    sideBarTitle,
    clear
  ])// eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  useEffect(() => {
    if (meetingList && !filter && !clear)
      setMeetingsData(meetingList)
  }, [meetingList, filter, clear])// eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className='bg-white rounded-2 p-4'>
        {!loading && meetingsData?.length === 0 ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} />
          </div>
        ) : (
          <div className="overflow-x-auto hide-scrollbar">
            <Table className='w-100 '>
              <thead className='custom-thead border-bottom'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='font-size-12 font-weight-400 w-100 table-row border-dark-mode '
                  >
                    {headerGroup.headers.map((header, index) => (
                      <th key={header.id} colSpan={header.colSpan} className=' pb-6 pt-4' style={{ minWidth: index === headerGroup.headers.length - 1 ? undefined : '250px' }}>
                        {header.isPlaceholder ? null : (
                          <div
                            className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <SkeletonRow count={8} />
                  </>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className='border-bottom'>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className='pt-4 border-0'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr >
                  ))
                )}
              </tbody >
            </Table >
          </div >
        )}
        {
          !loading && meetingsData?.length > 0 && (
            <div className='w-100 d-md-flex justify-content-between align-items-center px-0 mt-8'>
              <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
                <select
                  aria-label='Default select example'
                  className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark ${mode === 'dark' ? ' border-dark' : ''}`}
                  value={pagination.pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value)
                    setPagination((prev) => ({
                      ...prev,
                      pageSize: newSize,
                      pageIndex: 0, // Reset to first page when page size changes
                    }))
                  }}
                >
                  {[10, 20, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>

                <span className='font-size-13 text-center text-md-start'>
                  {formatMessage({
                    id: `Showing ${pagination.currentPage === 1
                      ? 1
                      : (pagination.currentPage - 1) * pagination.pageSize + 1
                      } to ${pagination.totalCount > pagination.currentPage * pagination.pageSize ? pagination.currentPage * pagination.pageSize : (pagination.totalCount - pagination.currentPage * pagination.pageSize) + pagination.currentPage * pagination.pageSize} of ${pagination.totalCount} records`,
                  })}
                </span>
              </div>

              <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
                <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                  {'<<'}
                </Pagination.First>

                <Pagination.Prev
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.prevPage}
                >
                  {'<'}
                </Pagination.Prev>

                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <Pagination.Item
                    key={i}
                    active={i === pagination.currentPage - 1}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}

                <Pagination.Next
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.nextPage}
                >
                  {'>'}
                </Pagination.Next>

                <Pagination.Last
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={!pagination.nextPage}
                >
                  {'>>'}
                </Pagination.Last>
              </Pagination>
            </div>
          )
        }
      </div >
      {activeModal === 'AddDocumentModal' && (
        <AddDocumentModal isActive={true} setIsActive={closeModal} meeting={meeting} />
      )}
      <DeleteMeetingConfirmationModal
        setDeleteShowModal={setShowCancelModal}
        showDeleteModal={showCancelModal}
        onDeleteSubmit={cancelMeeting}
        deleteLoading={cancelLoading}
      />
    </>
  )
}
